import { CouponApplyPhase } from "../interfaces/coupon-qualifier.interface";
import { BaseTriggerItemsQualifier } from "./base-trigger-items";
import { BogoTriggerItemSelector } from "./bogo/bogo-trigger-item-selector";
import { Util } from "../../../../util/util";
export class CategoryBaseTriggerItemsQualifier extends BaseTriggerItemsQualifier {
    phase = CouponApplyPhase.PreApply;
    _stopAtFirstMatch;
    constructor(sourceData, stopAtFirstMatch) {
        super(sourceData);
        this._stopAtFirstMatch = stopAtFirstMatch;
    }
    _getTriggerItems(coupon, cart) {
        return coupon.isBogo
            ? this._getBogoTriggerItems(coupon, cart)
            : this._getStandardTriggerItems(coupon, cart);
    }
    /**
     * Buy One Get One In Category (ADO #84406): both positions are filled from the order items in
     * the coupon's categories, so the categories are lent to the selector as the pool for BOTH the
     * buying and the discounted item. Returning [] means the coupon does not apply — nothing is
     * released because only this qualifier's cart copy was marked.
     */
    _getBogoTriggerItems(coupon, cart) {
        const pair = new BogoTriggerItemSelector(this._asBogoCandidateSource())
            .select(coupon, cart, coupon.itemCategoryIds, coupon.itemCategoryIds);
        return pair ? [pair.buying, pair.discounted] : [];
    }
    /** Lends the selector this qualifier's cart copy, with a set of item categories as the pool. */
    _asBogoCandidateSource() {
        return {
            cartItems: this._cartItems,
            collectCandidates: (categoryIds, coupon, cart) => this._collectBogoCandidates(categoryIds, coupon, cart)
        };
    }
    /**
     * Every unit still available in the categories, each marked as taken. A split item is looked at
     * once, under whichever half comes first, and every half is marked together.
     */
    _collectBogoCandidates(categoryIds, coupon, cart) {
        const candidates = [];
        const seenSplitKeys = new Set();
        for (const cartItem of this._cartItems) {
            if (!this._isInCategories(categoryIds, cartItem))
                continue;
            if (cartItem.halfCount > 1) {
                if (seenSplitKeys.has(cartItem.itemKey))
                    continue;
                seenSplitKeys.add(cartItem.itemKey);
            }
            while (this._takeBogoUnit(coupon, cartItem, cart)) {
                candidates.push(this._toCategoryTriggerItem(cartItem));
            }
        }
        return candidates;
    }
    /**
     * Marks one more unit of the item for Buy One Get One, or returns false once none is left.
     * `allowOtherCoupons` lets a unit another *permissive* coupon already discounted back in, but
     * never the same unit twice — otherwise the buying position could be filled from the discounted
     * item's line — and never a unit closed off by the two-sided rule.
     */
    _takeBogoUnit(coupon, cartItem, cart) {
        if (cartItem.discountedMarkedQuantity >= cartItem.quantity)
            return false;
        if (!this._isItemEligible(coupon, cartItem, cart))
            return false;
        if (cartItem.halfCount > 1) {
            if (!this._allHalvesQualify(coupon, cartItem, cart))
                return false;
            this._markAllHalves(cartItem.itemKey);
        }
        else {
            cartItem.discountedMarkedQuantity += 1;
        }
        return true;
    }
    _markAllHalves(itemKey) {
        for (const half of this._cartItems) {
            if (half.itemKey === itemKey)
                half.discountedMarkedQuantity += 1;
        }
    }
    _isInCategories(categoryIds, cartItem) {
        const mItem = this._data.items.find(x => x.itemId === cartItem.id);
        return !!mItem && categoryIds.includes(mItem.itemCategoryId);
    }
    _getStandardTriggerItems(coupon, cart) {
        const categoryItems = new Map();
        for (const item of this._cartItems) {
            const mItem = this._data.items.find(x => x.itemId === item.id);
            if (!mItem)
                continue;
            if (!coupon.itemCategoryIds.includes(mItem.itemCategoryId))
                continue;
            const arr = categoryItems.get(mItem.itemCategoryId);
            if (arr)
                arr.push(item);
            else
                categoryItems.set(mItem.itemCategoryId, [item]);
        }
        const validItems = [];
        for (const [_categoryId, cartItems] of categoryItems) {
            const validGroupItems = this._qualifyCouponItemsByCategory(coupon, cart, cartItems);
            if (!validGroupItems?.length)
                continue;
            validItems.push(...validGroupItems);
            if (this._stopAtFirstMatch)
                break;
        }
        return validItems;
    }
    _qualifyCouponItemsByCategory(coupon, cart, categoryCartItems) {
        const triggerItems = [];
        for (const cartItem of categoryCartItems) {
            if (!this._isItemEligible(coupon, cartItem, cart))
                continue;
            if (cartItem.halfCount > 1) {
                // ADO #82541: every half must independently meet the coupon's category
                // criteria before the whole item inherits the discount. A single
                // qualifying half no longer grants the discount to its siblings.
                if (!this._allHalvesQualify(coupon, cartItem, cart))
                    continue;
                cart.markAllHalves(this._cartItems, cartItem.itemKey);
            }
            else {
                cartItem.discountedMarkedQuantity += 1;
            }
            triggerItems.push(this._toCategoryTriggerItem(cartItem));
            if (this._stopAtFirstMatch)
                break;
        }
        return triggerItems;
    }
    /** Modifier count and add-on price are not supported on category qualifiers since they don't target specific items. */
    _toCategoryTriggerItem(cartItem) {
        return {
            index: this._cartItems.indexOf(cartItem),
            modifierCount: 0,
            addOnPrice: 0
        };
    }
    _isItemEligible(coupon, cartItem, cart) {
        if (coupon.excludeLoyaltyUsage && cartItem.isRedeemed)
            return false;
        return !cartItem.isRemoved && Util.IsItemUnitAvailable(cart, cartItem, coupon.allowOtherCoupons);
    }
    _isInQualifyingCategory(coupon, cartItem) {
        return this._isInCategories(coupon.itemCategoryIds, cartItem);
    }
    /**
     * A half & half item qualifies only when every half sharing its itemKey belongs
     * to a qualifying coupon category and is otherwise eligible. Enforces ADO #82541:
     * all halves must meet criteria rather than inheriting the discount from the first
     * qualifying half.
     */
    _allHalvesQualify(coupon, cartItem, cart) {
        const halves = this._cartItems.filter(x => x.itemKey === cartItem.itemKey);
        // All configured halves must be present in the cart.
        if (halves.length !== cartItem.halfCount)
            return false;
        return halves.every(half => this._isInQualifyingCategory(coupon, half) &&
            this._isItemEligible(coupon, half, cart));
    }
}
//# sourceMappingURL=category-base-trigger-items.js.map